import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Checkbox,
  Divider,
  Progress,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { EduModal } from '@/components/education/EduModal';
import useAppEvents from '@/patient-app-common/hooks/useAppEvents';
import useSegment from '@/hooks/useSegment';
import { useReadEventMutation } from '@/patient-app-common/api/patientApi';
import { AppEvent } from '@/patient-app-common/types/ApplicationEvent';

const EVENT = 'To do item click';

export function TodoList() {
  const { t } = useTranslation('todo');
  const { activeTodos } = useAppEvents();
  const { sendTrackEvent } = useSegment();

  const [readEvent] = useReadEventMutation();

  const percentage = useMemo(() => {
    if (!activeTodos.length) return 0;
    const tasksDone = activeTodos.filter((tasks) => tasks.done).length;

    return Math.round((tasksDone / activeTodos.length) * 100);
  }, [activeTodos]);

  let progressText = '';

  if (_.isNumber(percentage)) {
    progressText = t('todoheader.title', { percentage });
  }

  const handleTodoCheckbox = async (todo: AppEvent, index: number) => {
    const status = !activeTodos[index].done;

    const params = {
      eventId: todo.id,
      value: status.toString(),
    };

    try {
      await readEvent(params).unwrap();

      sendTrackEvent(EVENT, {
        id: todo.id,
        checked: todo.done,
        percentComplete: percentage,
        title: todo.title,
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      <div className="space-y-4 mb-8">
        {!_.isEmpty(activeTodos) && (
          <>
            <h1 className="font-light text-2xl">{progressText}</h1>
            <Progress
              aria-label={`${percentage}%`}
              color="success"
              value={percentage}
            />
          </>
        )}
      </div>
      {!_.isEmpty(activeTodos) && (
        <Card shadow="sm">
          <CardBody className="p-0">
            {activeTodos
              .sort((a, b) => Number(a.done) - Number(b.done))
              .map((event, i) => (
                <TodoEvent
                  key={event.title}
                  checkTodo={() => handleTodoCheckbox(event, i)}
                  event={event}
                />
              ))}
          </CardBody>
        </Card>
      )}
    </>
  );
}

interface TodoEventProps {
  event: AppEvent;
  checkTodo: () => void;
}

function TodoEvent({ checkTodo, event }: TodoEventProps) {
  const { t } = useTranslation('todo');
  const navigate = useNavigate();

  const [eduModal, toggleEduModal] = useState(false);

  const formatSubtitle = (initText: string) => {
    const today = t('common.text.today');
    let formattedText = today;
    if (initText.length > 0) {
      const subtitle = initText.charAt(0).toLowerCase() + initText.slice(1);
      formattedText = `${today}, ${subtitle}`;
    }
    return formattedText;
  };

  const subtitle = useMemo(() => {
    if (!event.repeating || !event.subtitle) return event.subtitle;
    return formatSubtitle(event.subtitle);
  }, [event]);

  return (
    <>
      <EduModal
        event={event}
        source="TodoEvent"
        onClose={() => toggleEduModal(false)}
        isOpen={eduModal}
      />
      <Card className="px-4 w-full" radius="none" shadow="none">
        <CardHeader className="flex justify-end px-0 py-1">
          {event.repeating && (
            <Icon
              aria-hidden="true"
              className="text-success-500 text-xl"
              icon="mdi:repeat"
            />
          )}
        </CardHeader>
        <CardBody className="pt-0 overflow-x-hidden">
          <Checkbox
            color="primary"
            className="font-semibold pt-4"
            isSelected={event.done ? true : false}
            onChange={checkTodo}
          >
            <span className={event.done ? 'line-through' : ''}>
              {event.title}
            </span>
          </Checkbox>
          <p className={`font-light pl-7 ${event.done ? 'line-through' : ''}`}>
            {subtitle}
          </p>
          {event.done && event.completedDate && (
            <p className="font-light text-sm pl-8">
              {t('common.text.completed_date', {
                date: dayjs(event.completedDate).format('LLLL'),
              })}
            </p>
          )}
        </CardBody>
        <CardFooter className="flex flex-col items-end">
          {!_.isEmpty(event.edIds) && (
            <Button
              color="primary"
              onPress={() => toggleEduModal(true)}
              variant="flat"
            >
              {event.options.linkText || t('common.link.learn_more')}
            </Button>
          )}
          {!_.isEmpty(event.options.id) && (
            <Button
              color="primary"
              onPress={() =>
                navigate(`/tracker/${event.options.id}`, {
                  state: {
                    eventId: event.id,
                  },
                })
              }
              variant="flat"
            >
              {event.options.linkText || t('common.link.add_entry')}
            </Button>
          )}
        </CardFooter>
      </Card>
      <Divider />
    </>
  );
}
